import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EncounterService } from './encounter.service';
import { InformationRaceService } from './information-race.service';
import { NegotiationService } from './negotiation.service';
import { RaceService } from "./race.service";
import { UserService } from './user.service';

import { Race } from "../interfaces/race";
import { User } from "../interfaces/user";
import { Campaign } from "../interfaces/campaign";

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private encounterService = inject(EncounterService);
  private informationRaceService = inject(InformationRaceService);
  private negotiationService = inject(NegotiationService);
  private raceService = inject(RaceService);
  private userService = inject(UserService);

  public activeTab: number = 1;
  public activeProperty: number = 0;
  public antamonyShow: boolean = false;
  public areYouSureShow: boolean = false;
  public backendVersion: Signal<string | undefined>;

  public bannerAlertMessage: string = undefined; //'Alert Message Goes Here';
  public bannerErrorMessage: string = undefined; //'Error Message Goes Here';
  public bannerNotifyMessage: string = undefined; //Notify Message Goes Here

  public campaign: any;
  public closeModalPage: boolean = false;
  public frontendVersion: string;

  public ismTesting: boolean = false;
  public isFirstTurn: Signal<boolean> = signal<boolean>(false);
  public isEconomicsActive = true;
  public isMobileDevice = false;

  public menuLeftVisible: boolean = true;
  public menuRightVisible: boolean = false;
  public menuTestingVisible: boolean = false;

  public menuMobileLeftVisible: boolean = false;
  public menuMobileCenterVisible: boolean = false;
  public menuMobileRightVisible: boolean = false;
  selectedCampaignIndex: number = -1;

  public race: Race | undefined;
  public showPassword = false;
  public showEmptyRows = false;
  private tableCache = new Map();

  public turnCampaign: number = 1;
  private firstTurn: number = 1;
  public turnEdit: WritableSignal<number> = signal<number>(1);
  public turnMin: number = 1;
  public turnMax: number = 7;
  public user: User | undefined;
  busyCounter: number = 0;
  busy: boolean = false;
  minTurn: number = 1;
  maxTurn: number = 1;
  users: Signal<User[] | undefined>;
  countNonHostileRaces: number = 0;
  hasNegotiations: boolean = false;
  hasEncounters: boolean = false;

  constructor (
  ) {
    this.backendVersion = signal("0.0.999");
    this.frontendVersion = "0.0.999";
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.isFirstTurn = computed(() => this.firstTurn === this.turnEdit());
  };

  testingOnClick () {
    this.menuTestingVisible = !this.menuTestingVisible;
  };


  setBusy (isBusy: boolean, message?: string) {
    this.busyCounter = this.busyCounter + (isBusy ? 1 : -1);
    setTimeout(() => { this.busy = isBusy; }, 0);
  };
  iAmBusy (message?: string) {
    this.setBusy(true, message);
  };
  iAmNotBusy (message?: string) {
    this.setBusy(false, message);
  };

  getUsers (): User[] | undefined {
    if (!this.users) {
      this.users = toSignal(this.userService.getAllUsers());
    }
    return this.users();
  };

  setSelectedCampaignIndex (index: number) {
    this.selectedCampaignIndex = index;
    localStorage.setItem('selectedCampaignIndex' + this.user?._id, JSON.stringify(index));
  };

  clearSelectedCampaignIndex () {
    this.selectedCampaignIndex = -1;
    localStorage.removeItem('selectedCampaignIndex' + this.user?._id);
  };

  getSelectedCampaignIndex (): number {
    if (this.selectedCampaignIndex == null || this.selectedCampaignIndex < 0) {
      this.selectedCampaignIndex = -1;
      let storedValue = localStorage.getItem('selectedCampaignIndex' + this.user?._id);
      if (storedValue) {
        this.selectedCampaignIndex = JSON.parse(storedValue);
      }
    }
    return this.selectedCampaignIndex;
  };

  setCampaign (campaign: Campaign | undefined) {
    let changed = false;
    if (campaign) {
      changed = !this.campaign || (this.campaign._id !== campaign._id);
      localStorage.setItem('selectedCampaignId', campaign._id);
    }
    else {
      localStorage.removeItem('selectedCampaignId');
    }
    this.campaign = campaign;
    if (campaign) {
      this.turnCampaign = campaign.turn;
      this.turnEdit.set(campaign.turn);
    }
    return changed;
  };

  getCampaign (): Campaign | undefined {
    if (this.campaign == null) {
      let selectedCampaignId = localStorage.getItem('selectedCampaignId');
      if (selectedCampaignId) {
        let selectedCampaignObject = localStorage.getItem(selectedCampaignId);
        if (selectedCampaignObject) {
          this.campaign = JSON.parse(selectedCampaignObject);
        }
      }
    }
    return this.campaign;
  };

  setRace (race: Race | undefined) {
    let newId = race?._id || 'nope';
    let oldId = this.race?._id || 'nope';

    let changed = (newId !== 'nope' && (newId !== oldId));
    if (race) {
      this.race = race;
      this.setTurnData(race.turnActivated || 1);
      localStorage.setItem('selectedRaceId', race._id);
      if (changed) {
        this.informationRaceService.getNonHostileRaceCountForRaceId$(race._id).subscribe(
          count => this.countNonHostileRaces = count
        );
        this.negotiationService.getNegotiationCountForRaceId$(race._id).subscribe(
          count => { this.hasNegotiations = (count > 0); }
        );
        this.encounterService.getEncounterCountForRaceId$(race._id).subscribe(
          count => this.hasEncounters = count > 0
        );
      }
    }
    else {
      localStorage.removeItem('selectedRaceId');
    }
    return changed;
  };

  getRace (): Race | undefined {
    if (this.race == null) {
      let selectedRaceId = localStorage.getItem('selectedRaceId');
      if (selectedRaceId) {
        let selectedRaceObject = localStorage.getItem(selectedRaceId);
        if (selectedRaceObject) {
          this.race = JSON.parse(selectedRaceObject);
        }
      }
    }
    return this.race;
  };

  preloadCampaignData (campaignId: string) {
    if (campaignId == null) {
      throw new Error("No Active Campaign");
    }
    this.raceService.getRacesForCampaignId(campaignId);
  };

  setCampaignAndRace (campaign: Campaign, race: Race) {
    let campaignChanged = this.setCampaign(campaign);
    if (campaignChanged) {
      // preload campaign data
      this.preloadCampaignData(campaign._id);
    }
    this.setRace(race);
  };

  setNotifyMessage (message: string, dismissTiming: number = 3500) {
    this.bannerNotifyMessage = message;
    if (dismissTiming > 0) {
      setTimeout(() => {
        this.bannerNotifyMessage = undefined;
      }, dismissTiming);
    }
  };

  transactionSaved () {
    this.setNotifyMessage('Transaction Saved');
  };

  transactionDeleted () {
    this.setNotifyMessage('Transaction Deleted');
  };

  setAlertMessage (message: string, dismissTiming: number = 3500) {
    this.bannerAlertMessage = message;
    if (dismissTiming > 0) {
      setTimeout(() => {
        this.bannerAlertMessage = undefined;
      }, dismissTiming);
    }
  };

  setErrorMessage (message: string, dismissTiming: number = 3500) {
    this.bannerErrorMessage = message;
    if (dismissTiming > 0) {
      setTimeout(() => {
        this.bannerErrorMessage = undefined;
      }, dismissTiming);
    }
  };

  setTurnData (turnActivated: number) {
    this.firstTurn = turnActivated;
    this.isEconomicsActive = (turnActivated <= this.turnCampaign);
    this.minTurn = this.turnCampaign - 6;
    if (this.minTurn < 0 || this.minTurn <= turnActivated) {
      this.minTurn = turnActivated;
    }
    this.maxTurn = this.turnCampaign + 6;
  };

  closeMobileMenus (event: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.menuMobileLeftVisible = false;
    this.menuMobileCenterVisible = false;
    this.menuMobileRightVisible = false;
    this.menuTestingVisible = false;
  };

  resetCampaignData (): void {
    this.closeMobileMenus(undefined);

    this.setRace(undefined);
    this.setCampaign(undefined);
  };

  resetAllData (): void {
    this.resetCampaignData();

    // Clear all cached info
    localStorage.clear();
  };

  toggleISMtesting () {
    this.ismTesting = !this.ismTesting;
  };
}
